import { useCallback, useMemo, useState } from "react";
import axios from "axios";
import { composeApiURL } from "helpers/utility/axiosHelpers";
import { ProgressCardProps } from "views/components/LabelPropagation/ProgressStep/ProgressCard";
import { ProgressCardsProps } from "views/components/LabelPropagation/ProgressStep";
import { useLocation, useNavigate } from "react-router";
import {
  LABEL_CLASS_PARAMETER,
  REVIEW_PARAMETER,
} from "../../helpers/constants/miscellaneous";
import { ProgressBarProps } from "views/uikit/ProgressBars/VLProgressBar";

export interface LabelStatsData extends StatData {
  category_display_name: string;
  label_category_id: string;
}

interface StatData {
  cycle_questions: number;
  cycle_questions_answered: number;
  goal: number;
  total_annotated: number;
  total_annotated_toward_goal: number;
}

export interface StatsData {
  stats: StatData;
  label_stats: LabelStatsData[];
  total_media_count: number;
}

export type StatsState = {
  mainProgress: ProgressBarProps;
  labels: ProgressCardsProps;
  review: ProgressCardsProps;
};

// ---------- helpers ----------

const mapLabelItems = (data: LabelStatsData[]): ProgressCardProps[] =>
  data.map((stat) => ({
    label: stat.category_display_name,
    maxItems: stat.goal,
    itemLength: stat.total_annotated_toward_goal,
    id: stat.label_category_id,
  }));

const mapReviewItems = (
  data: LabelStatsData[],
  labelCategoryId: string | null,
  onClick: (id: string) => void,
): ProgressCardProps[] =>
  data.map((stat) => ({
    label: stat.category_display_name,
    maxItems: stat.cycle_questions,
    itemLength: stat.cycle_questions_answered,
    active: labelCategoryId === stat.label_category_id,
    onClick,
    id: stat.label_category_id,
  }));

const toProgressCardsBase = (
  maxItems: number,
  itemLength: number,
  items: ProgressCardProps[],
  totalCount: string,
): ProgressCardsProps => ({
  mainProgress: {
    maxItems,
    itemLength,
    label: "",
    id: "",
  },
  totalCount,
  items,
});

const toLabelProgressCards = (
  main: StatData,
  items: ProgressCardProps[],
): ProgressCardsProps => {
  const percent =
    main.goal > 0
      ? Math.round((main.total_annotated_toward_goal / main.goal) * 100)
      : 0;

  return toProgressCardsBase(
    main.goal,
    main.total_annotated_toward_goal,
    items,
    `${percent}%`,
  );
};

const toReviewProgressCards = (
  main: StatData,
  items: ProgressCardProps[],
): ProgressCardsProps => {
  return toProgressCardsBase(
    main.cycle_questions,
    main.cycle_questions_answered,
    items,
    (main.cycle_questions - main.cycle_questions_answered).toString(),
  );
};

// ---------- hook ----------

export const useGetStats = () => {
  const [rawData, setRawData] = useState<StatsData | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState<boolean>();
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const labelId = searchParams.get(LABEL_CLASS_PARAMETER);

  const navigate = useNavigate();
  const onReviewClicked = useCallback(
    (id: string) => {
      const params = new URLSearchParams(location.search);
      params.set(LABEL_CLASS_PARAMETER, id);
      params.set(REVIEW_PARAMETER, "true");
      navigate(`${location.pathname}?${params.toString()}`);
    },
    [location.search, navigate, location.pathname],
  );

  const fetchStats = useCallback(async (datasetId: string, flowId: string) => {
    try {
      setLoading(true);
      const statsUrl = composeApiURL(
        process.env.REACT_APP_API_ENDPOINT!,
        `/flywheel/${datasetId}/stats/${flowId}`,
      );
      const { data } = await axios.get<StatsData>(statsUrl);
      setRawData(data);
      return data;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const stats: StatsState | undefined = useMemo(() => {
    if (!rawData) return undefined;

    const labels = toLabelProgressCards(
      rawData.stats,
      mapLabelItems(rawData.label_stats),
    );

    const review = toReviewProgressCards(
      rawData.stats,
      mapReviewItems(rawData.label_stats, labelId, onReviewClicked),
    );

    const mainProgressItemsLength = rawData.stats.total_annotated;
    const mainProgressMaxItems = rawData.total_media_count;

    const mainProgress: ProgressBarProps = {
      maxItems: mainProgressMaxItems,
      itemLength: mainProgressItemsLength,
      color:
        mainProgressItemsLength < mainProgressMaxItems ? "danger" : undefined,
    };

    return { labels, review, mainProgress };
  }, [rawData, labelId, onReviewClicked]);

  return { stats, fetchStats, error, loading };
};
